import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface MapContainerProps {
    center?: [number, number];
    zoom?: number;
    children?: React.ReactNode;
    onMapReady?: (map: ReturnType<typeof L.map>) => void;
    style?: React.CSSProperties;
    className?: string;
}

/**
 * 地图容器组件
 * 封装 Leaflet 地图初始化逻辑
 */
const MapContainer: React.FC<MapContainerProps> = ({
    center = [31.49, 120.31], // 默认无锡
    zoom = 14,
    children,
    onMapReady,
    style,
    className,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<ReturnType<typeof L.map> | null>(null);

    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;

        // 初始化地图
        const map = (L as any).map(containerRef.current, {
            center,
            zoom,
            zoomControl: true,
            attributionControl: false,
        });

        // 添加地图图层 (高德地图)
        (L as any).tileLayer(
            'https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}',
            {
                subdomains: ['1', '2', '3', '4'],
                maxZoom: 18,
            }
        ).addTo(map);

        mapRef.current = map;

        // 通知地图准备完成
        onMapReady?.(map);

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    // 更新中心点
    useEffect(() => {
        if (mapRef.current && center) {
            mapRef.current.setView(center, zoom);
        }
    }, [center, zoom]);

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                width: '100%',
                height: '100%',
                minHeight: 400,
                ...style,
            }}
        >
            {children}
        </div>
    );
};

export default React.memo(MapContainer);
